
import type { PropType } from 'vue'

import type { ClusterFeatureEntity, DispatcherWorkplaceDelivery, DispatcherWorkplaceTake } from '@/modules/DispatcherWorkplaceModule/types'
import type { ClusterFeature } from '@/modules/YandexMap3Module/map-cluster.types'

import { defineComponent, reactive, onMounted } from 'vue'

import DwClusterPopupGroup from './Group.vue'
import DwClusterPopupSelectedFeature from './SelectedFeature.vue'

export default defineComponent({
    components: {
        'dw-cluster-popup-group': DwClusterPopupGroup,
        'dw-cluster-popup-selected-feature': DwClusterPopupSelectedFeature
    },
    props: {
        features: {
            type: Array as PropType<Array<ClusterFeature<ClusterFeatureEntity>>>,
            default: () => []
        },
        selectedFeatureId: {
            type: Number as PropType<number | null>,
            default: null
        },
        isCourierItem: {
            type: Boolean,
            default: false,
        }
    },
    emits: [
        'close',
        'block-scroll',
        'unlock-scroll',
        'change-date',
        'add-to-wait-list',
        'assign',
        'self-delivery'
    ],
    setup(props, { emit }) {
        const state = reactive({
            is_feature_selected: false,
            selected_feature_index: null as number | null,
            selected_feature: null as ClusterFeature<ClusterFeatureEntity> | null,
        })

        const selectFeature = (options: {
            feature: ClusterFeature<ClusterFeatureEntity>
            feature_index: number
        }) => {
            state.selected_feature = options.feature
            state.selected_feature_index = options.feature_index
            state.is_feature_selected = true
        }

        const deselectFeature = () => {
            state.is_feature_selected = false
            state.selected_feature = null
            state.selected_feature_index = null
        }

        const onSelectPrevious = () => {
            if (state.selected_feature_index === 0) {
                state.selected_feature_index = props.features.length - 1
            } else {
                state.selected_feature_index = (state.selected_feature_index as number) - 1
            }

            state.selected_feature = props.features[state.selected_feature_index]
        }

        const onSelectNext = () => {
            if (state.selected_feature_index === props.features.length - 1) {
                state.selected_feature_index = 0
            } else {
                state.selected_feature_index = (state.selected_feature_index as number) + 1
            }

            state.selected_feature = props.features[state.selected_feature_index]
        }

        const onClose = () => {
            emit('close')
        }

        const onChangeDate = (feature: ClusterFeature<ClusterFeatureEntity>) => {
            emit('change-date', feature)
        }

        const onAddToWaitList = (feature: ClusterFeature<ClusterFeatureEntity>) => {
            emit('add-to-wait-list', feature)
        }

        const onAssign = (feature: ClusterFeature<ClusterFeatureEntity>) => {
            emit('assign', feature)
        }

        const onSelfDelivery = (feature: ClusterFeature<ClusterFeatureEntity>) => {
            emit('self-delivery', feature)
        }

        const featureFinder = (feature: ClusterFeature<ClusterFeatureEntity>) => {
            if (feature.entity) {
                if (feature.entity.type === 1) {
                    return (feature.entity as DispatcherWorkplaceTake).orderId === props.selectedFeatureId
                }

                if (feature.entity.type === 2) {
                    return (feature.entity as DispatcherWorkplaceDelivery).invoiceId === props.selectedFeatureId
                }
            }

            return false
        }

        onMounted(() => {
            if (props.selectedFeatureId) {
                const feature = props.features.find(featureFinder)
                const featureIndex = props.features.findIndex(featureFinder)

                if (feature && featureIndex >= 0) {
                    selectFeature({ feature, feature_index: featureIndex })
                }
            }
        })

        return {
            state,
            selectFeature,
            deselectFeature,
            onSelectPrevious,
            onSelectNext,
            onClose,
            onChangeDate,
            onAddToWaitList,
            onAssign,
            onSelfDelivery,
        }
    }
})
